export const MAX_PUBLIC_BUDGET = 100000;
export const PUBLIC_BUDGET_PRESETS = [5, 10, 20, 50, 100, 250, 500];

export const parsePublicBudgetInput = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return { ok: false, value: null, error: "Enter a budget greater than zero." };
  }

  const normalised = raw.replace(/^\u00a3\s*/, "").replace(/,/g, "").trim();
  const parsed = Number(normalised);
  if (!Number.isFinite(parsed)) {
    return { ok: false, value: null, error: "Enter a valid budget amount." };
  }
  if (parsed <= 0) {
    return { ok: false, value: null, error: "Enter a budget greater than zero." };
  }
  if (parsed > MAX_PUBLIC_BUDGET) {
    return {
      ok: false,
      value: null,
      error: `Enter a budget of \u00a3${MAX_PUBLIC_BUDGET.toLocaleString("en-GB")} or less.`,
    };
  }

  return { ok: true, value: Math.round(parsed * 100) / 100, error: "" };
};

export const sanitisePublicBudget = (value, fallback = 20) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.min(MAX_PUBLIC_BUDGET, Math.round(parsed * 100) / 100);
};

export const currentTicketPrice = (comp = {}) => {
  const value = comp.current_unit_ticket_price ?? comp.ticket_price;
  if (value == null || (typeof value === "string" && value.trim() === "")) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

export const hasFreeOrPostalEntryMethod = (comp = {}) => (
  comp.free_or_postal_entry_method_available === true ||
  (comp.free_entry_available === true && Boolean(comp.free_entry_method_summary))
);

export const freeOrPostalEntryText = (comp = {}) => (
  comp.free_or_postal_entry_method_text || comp.free_entry_method_summary || ""
);

export const freeOrPostalEntryUrl = (comp = {}) => (
  comp.free_or_postal_entry_method_url || comp.free_entry_method_url || ""
);

export const publicClosingCountdown = (comp = {}, now = Date.now()) => {
  const closing = comp?.closing_date;
  if (comp?.closing_date_trusted !== true || typeof closing !== "string") return null;
  // Only a trusted entry-close instant is suitable for a countdown, never a draw date.
  const match = closing.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/);
  if (
    !match || Number(match[2]) > 23 || Number(match[3]) > 59 || Number(match[4] || 0) > 59
  ) return null;
  const day = new Date(`${match[1]}T00:00:00Z`);
  const closesAt = Date.parse(closing);
  if (
    !Number.isFinite(closesAt) || !Number.isFinite(now) || Number.isNaN(day.getTime())
    || day.toISOString().slice(0, 10) !== match[1]
  ) return null;
  const remaining = closesAt - now;
  if (remaining <= 0 || remaining > 3 * 24 * 60 * 60 * 1000) return null;
  const minutes = Math.floor(remaining / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const duration = [
    days && `${days}d`, hours && `${hours}h`, minutes % 60 && `${minutes % 60}m`,
  ].filter(Boolean).join(" ");
  return {
    label: `Closing in ${duration || "under 1m"}`,
    dateTime: closing,
    ukTime: new Date(closesAt).toLocaleString("en-GB", {
      timeZone: "Europe/London",
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZoneName: "short",
    }),
  };
};

const safeOddsText = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const lower = text.toLowerCase();
  if (
    lower === "unknown"
    || lower.includes("unavailable")
    || lower.includes("undefined")
    || lower.includes("nan")
    || /\b1\s+in\s+0\b/.test(lower)
    || /^0(?:\.0+)?%$/.test(lower)
  ) {
    return "";
  }
  return text;
};

const awarenessOnly = (comp = {}) => (
  comp.mckinney_awareness_only === true
  || comp.mckinney_standard_odds_supported === false
  || comp.llf_awareness_only === true
  || comp.llf_standard_odds_supported === false
  || comp.kilted_awareness_only === true
  || comp.kilted_standard_odds_supported === false
  || comp.ukcc_awareness_only === true
  || comp.ukcc_standard_odds_supported === false
  || comp.bounty_awareness_only === true
  || comp.bounty_standard_odds_supported === false
  || comp.apex_awareness_only === true
  || comp.apex_standard_odds_supported === false
  || comp.jammy_awareness_only === true
  || comp.jammy_standard_odds_supported === false
  || comp.pawsome_awareness_only === true
  || comp.pawsome_standard_odds_supported === false
  || comp.lincolnshire_awareness_only === true
  || comp.lincolnshire_standard_odds_supported === false
  || comp.drift_awareness_only === true
  || comp.drift_standard_odds_supported === false
  || comp.palladium_awareness_only === true
  || comp.palladium_standard_odds_supported === false
  || comp.pristine_awareness_only === true
  || comp.pristine_standard_odds_supported === false
  || comp.giveawayguys_awareness_only === true
  || comp.giveawayguys_standard_odds_supported === false
  || comp.storm_awareness_only === true
  || comp.storm_standard_odds_supported === false
  || comp.redline_awareness_only === true
  || comp.redline_standard_odds_supported === false
  || comp.prizeparadise_awareness_only === true
  || comp.prizeparadise_standard_odds_supported === false
  || comp.caddy_awareness_only === true
  || comp.caddy_standard_odds_supported === false
  || comp.hotcomps_awareness_only === true
  || comp.hotcomps_standard_odds_supported === false
  || comp.thatprizeguy_awareness_only === true
  || comp.thatprizeguy_standard_odds_supported === false
  || comp.bright_awareness_only === true
  || comp.bright_standard_odds_supported === false
  || comp.salexmo_awareness_only === true
  || comp.salexmo_standard_odds_supported === false
  || comp.click_awareness_only === true
);

export const publicOddsUnavailableReason = (comp = {}, ticketCapReason = "") => {
  if (awarenessOnly(comp)) {
    return "Awareness-only listing - standard odds are not available";
  }
  if (
    String(comp.botb_competition_family || "").toLowerCase() === "skill_based"
    || String(comp.competition_mechanic || "").toLowerCase() === "spot_the_ball"
  ) {
    return "No standard odds available";
  }
  const evidence = `${comp.odds_unavailable_reason || ""} ${comp.odds_model_status || ""} ${ticketCapReason || ""}`
    .toLowerCase()
    .replace(/[_-]+/g, " ");
  if (evidence.includes("current entr")) {
    return "Current entry count unavailable";
  }
  if (
    evidence.includes("max ticket")
    || evidence.includes("ticket cap")
    || evidence.includes("total ticket")
  ) {
    return "Ticket cap unavailable";
  }
  if (evidence.includes("ticket price")) {
    return "Ticket price unavailable";
  }
  return "Source does not expose sufficient data";
};

export const publicOddsState = (comp = {}, capInfo = {}) => {
  const estimatedOdds = safeOddsText(comp?.scores?.estimated_odds);
  const hasTrustedCap = Boolean(
    capInfo?.totalTicketCap && capInfo?.ticketCapTrusted !== false
  );
  const isReady = Boolean(
    !awarenessOnly(comp)
    && comp.odds_ready !== false
    && hasTrustedCap
    && estimatedOdds
  );
  const reason = isReady
    ? ""
    : publicOddsUnavailableReason(comp, capInfo?.ticketCapReason);
  return {
    isReady,
    displayText: isReady ? estimatedOdds : "Odds unavailable",
    reason,
    ariaLabel: isReady ? `Estimated odds ${estimatedOdds}` : `Odds unavailable. ${reason}`,
  };
};
